/**
 * TraceDrillDownModal — shared click-to-modal drill-down for the clickable
 * charts on the Overview / Quality / Operations tabs.
 *
 * v1.4 UX fix (2026-04-24): the inner table is now `TraceTable`, the same
 * component used by TimeSavedBreakdownModal — "нужен унифицированный
 * компонент". Mobile and desktop layouts, column widths, sort behaviour and
 * cell rendering all come from that single source of truth.
 *
 * One modal backs four call sites:
 *   - Overview → Rating donut (click a segment)
 *   - Quality  → Rating distribution bar (click a bar)
 *   - Quality  → Latency distribution bar (click a band)
 *   - Operations → Complexity donut (click a segment)
 *
 * Data source: `useTracesList(filters, extras, enabled)` pointed at
 * `/salescoach/api/traces/list`. Same global FilterBar params apply, so totals
 * reconcile with the chart the modal opened from.
 *
 * Phase 9: row expand-collapse is enabled here for ALL four call-sites
 * (K-07 — intentional). The expanded-row body shows full input + output
 * via TraceTable.tsx's existing LazyTraceDetail, capped server-side at
 * 1000 chars (listTraces SELECT) — see spec §"Expand/collapse in the
 * modal". The new SkillsPage row click is the fifth call-site.
 */

import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  TraceTable,
  sortRows,
  type TraceColumnKey,
  type TraceTableSort,
  type TraceSortKey,
} from '@/components/TraceTable';
import { useTracesList } from '@/hooks/use-traces-list';
import type { Filters } from '@/lib/filters-schema';
import { tracelistRowToTableRow } from '@/lib/trace-row';
import type { TraceListExtras } from '@/services/api-types';

interface TraceDrillDownModalProps {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  /** Which slice of the chart was clicked (rating / latencyBand / …). */
  extras: TraceListExtras;
  /** Shown in the dialog header, e.g. "Traces rated 1 star". */
  title: string;
  /** Sub-line — typically the chart/tab name + context the user opened from. */
  subtitle?: string;
  /** Scopes `data-testid` attributes for e2e hooks. */
  testIdPrefix?: string;
}

const COLUMNS: TraceColumnKey[] = [
  'time',
  'user',
  'role',
  'kind',
  'complexity',
  'rating',
  'latency',
  'cost',
  'queryAnswer',
  'feedback',
];

const DEFAULT_SORT: TraceTableSort = { key: 'timestamp', dir: 'desc' };

export function TraceDrillDownModal({
  open,
  onClose,
  filters,
  extras,
  title,
  subtitle,
  testIdPrefix = 'trace-drilldown-modal',
}: TraceDrillDownModalProps): React.ReactElement {
  // Fetch at max page so client-side sort covers everything the server would
  // return. `total` still counts the unpaginated population so the footer can
  // call out truncation.
  const query = useTracesList(filters, { ...extras, limit: 500 }, open);

  const [sort, setSort] = React.useState<TraceTableSort>(DEFAULT_SORT);

  // Phase 9 (K-08) — expand state lives in the modal, not the consumer.
  // Reset to empty on each `open: false → true` transition so reopening
  // the modal for a different skill / band / tier never carries forward
  // expanded rows from the previous slice. The existing sort-reset
  // useEffect below uses the same trigger.
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(
    () => new Set(),
  );

  // Reset sort when the extras change (user clicked a different chart slice).
  const extrasKey = React.useMemo(() => JSON.stringify(extras), [extras]);
  React.useEffect(() => {
    if (open) {
      setSort(DEFAULT_SORT);
      setExpandedIds(new Set());
    }
  }, [open, extrasKey]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) onClose();
    },
    [onClose],
  );

  const onSort = React.useCallback((key: TraceSortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : {
            key,
            dir:
              key === 'timestamp' || key === 'name' || key === 'username'
                ? 'asc'
                : 'desc',
          },
    );
  }, []);

  const handleToggleExpanded = React.useCallback((traceId: string) => {
    setExpandedIds((prev) => {
      // Immutable update — TraceTable expects a new Set reference each
      // toggle so its own re-render branches on identity, not contents.
      const next = new Set(prev);
      if (next.has(traceId)) {
        next.delete(traceId);
      } else {
        next.add(traceId);
      }
      return next;
    });
  }, []);

  const rawRows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const rows = React.useMemo(
    () => rawRows.map(tracelistRowToTableRow),
    [rawRows],
  );
  const sorted = React.useMemo(() => sortRows(rows, sort), [rows, sort]);
  const truncated = total > rawRows.length;
  // RM / Non-RM split is computed over the fetched page only — if the server
  // truncated at 500 rows, the split reflects the same capped subset the
  // user sees in the table. Source field: `TraceListRow.isRm` (backed by the
  // `user_role_cache` LEFT JOIN in `listTraces`).
  const rmCount = rawRows.filter((r) => r.isRm).length;
  const nonRmCount = rawRows.length - rmCount;

  const from = filters.from ?? undefined;
  const to = filters.to ?? undefined;

  const footer =
    query.data && sorted.length > 0 ? (
      <>
        Showing{' '}
        <span className="font-semibold text-foreground">{sorted.length}</span>{' '}
        of{' '}
        <span className="font-semibold text-foreground">{total}</span>{' '}
        trace{total === 1 ? '' : 's'}
        <span className="mx-1">·</span>
        RM{' '}
        <span className="font-semibold text-foreground">{rmCount}</span>
        {' · '}
        Non-RM{' '}
        <span className="font-semibold text-foreground">{nonRmCount}</span>
        {truncated && (
          <span className="ml-1 text-amber-600 dark:text-amber-400">
            · capped — narrow the filters for the full list
          </span>
        )}
      </>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={testIdPrefix}
        className="max-w-[min(96vw,80rem)] w-[min(96vw,80rem)] max-h-[88vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle data-testid={`${testIdPrefix}-title`}>
            {title}
          </DialogTitle>
          <DialogDescription
            data-testid={`${testIdPrefix}-subtitle`}
            className="text-xs text-muted-foreground"
          >
            {subtitle ??
              'Every trace matching the current filters and this chart slice. Click a column header to sort.'}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <TraceTable
            rows={sorted}
            columns={COLUMNS}
            sort={sort}
            onSort={onSort}
            from={from}
            to={to}
            isLoading={query.isLoading}
            isError={query.isError}
            onRetry={() => void query.refetch()}
            emptyMessage="No traces match this slice for the current filters."
            testIdPrefix={testIdPrefix}
            expandable
            expandedIds={expandedIds}
            onToggleExpanded={handleToggleExpanded}
          />
        </div>

        {footer && (
          <div
            data-testid={`${testIdPrefix}-footer`}
            className="border-t border-border pt-2 text-xs tabular-nums text-muted-foreground"
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
